// Herbe haute autour du joueur : touffes de brins instanciées par carrés de 16 m, seulement sur l'herbe
// (pas sur la route, les sols, dans les bâtiments ni sous les objets). Les carrés proches sont créés à la demande
// puis gardés ; ceux qui s'éloignent sont masqués (la brume cache la limite).
import * as THREE from 'three';
import type { GameMap } from '../shared/map';
import { windy } from './lowpoly';

const CHUNK = 16, CELL = 0.5, MARGIN = 24, VIEW = 58;
const PER_CELL = 0.55; // touffes par case de 0,5 m (en moyenne)

// Touffe : quelques brins fins (triangles), sombres au pied, clairs en haut ; normales vers le ciel (éclairée comme le sol)
function tuftGeometry() {
  const pos: number[] = [], col: number[] = [];
  const blades = 5;
  for (let i = 0; i < blades; i++) {
    const a = (i / blades) * Math.PI + Math.random() * 0.4, lean = 0.08 + Math.random() * 0.12;
    const h = 0.32 + Math.random() * 0.22, w = 0.045;
    const ox = (Math.random() - 0.5) * 0.12, oz = (Math.random() - 0.5) * 0.12;
    const cx = Math.cos(a), cz = Math.sin(a), lx = -cz * lean, lz = cx * lean;
    // Les deux faces (sens inverse) : vues de dos, elles restent éclairées comme de face
    pos.push(ox - cx * w, 0, oz - cz * w, ox + cx * w, 0, oz + cz * w, ox + lx, h, oz + lz);
    pos.push(ox + cx * w, 0, oz + cz * w, ox - cx * w, 0, oz - cz * w, ox + lx, h, oz + lz);
    col.push(0.42, 0.42, 0.42, 0.42, 0.42, 0.42, 1, 1, 1, 0.42, 0.42, 0.42, 0.42, 0.42, 0.42, 1, 1, 1);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(pos.map((_, i) => (i % 3 === 1 ? 1 : 0)), 3));
  return g;
}

const hash = (x: number, y: number, s: number) => { const h = Math.sin(x * 127.1 + y * 311.7 + s * 74.7) * 43758.5453; return h - Math.floor(h); };

export class Grass {
  private mask: Uint8Array;
  private cols: number; private rows: number; private ox = -MARGIN; private oy = -MARGIN;
  private chunks = new Map<string, THREE.InstancedMesh | null>();
  private geo = tuftGeometry();
  private mat = windy(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 1 }), 0, 0.22, 'grass');
  private root = new THREE.Group();

  constructor(map: GameMap, scene: THREE.Scene) {
    this.cols = Math.ceil((map.width + MARGIN * 2) / CELL); this.rows = Math.ceil((map.height + MARGIN * 2) / CELL);
    this.mask = new Uint8Array(this.cols * this.rows).fill(1);
    // Tout ce qui n'est pas de l'herbe est retiré du masque (rectangles rastérisés, avec une petite marge)
    const clear = (x0: number, y0: number, x1: number, y1: number, pad = 0.2) => {
      const c0 = Math.max(0, Math.floor((x0 - pad - this.ox) / CELL)), c1 = Math.min(this.cols - 1, Math.floor((x1 + pad - this.ox) / CELL));
      const r0 = Math.max(0, Math.floor((y0 - pad - this.oy) / CELL)), r1 = Math.min(this.rows - 1, Math.floor((y1 + pad - this.oy) / CELL));
      for (let r = r0; r <= r1; r++) this.mask.fill(0, r * this.cols + c0, r * this.cols + c1 + 1);
    };
    for (const f of map.floors) clear(f.x0, f.y0, f.x1, f.y1, 0.1);
    for (const b of map.buildings) clear(b.x0, b.y0, b.x1, b.y1, 0.3);
    for (const r of map.rects) if (r.look !== 'chainlink') clear(r.x0, r.y0, r.x1, r.y1);
    for (const l of map.lines) if (l.rail) clear(l.x0, l.y0 - 1.6, l.x1, l.y1 + 1.6);
    for (const p of map.props) clear(p.x - 0.4, p.y - 0.4, p.x + 0.4, p.y + 0.4, 0); // pied des arbres, poteaux…
    for (const e of map.extracts) clear(e.x - 1, e.y - 1, e.x + 1, e.y + 1, 0);
    this.root.name = 'grass';
    scene.add(this.root);
  }

  private build(cx: number, cy: number) {
    const mats: THREE.Matrix4[] = [], colors: THREE.Color[] = [];
    const q = new THREE.Quaternion(), up = new THREE.Vector3(0, 1, 0), s = new THREE.Vector3(), p = new THREE.Vector3();
    const n = CHUNK / CELL;
    for (let j = 0; j < n; j++) for (let i = 0; i < n; i++) {
      const wx = cx * CHUNK + i * CELL, wy = cy * CHUNK + j * CELL;
      const c = Math.floor((wx - this.ox) / CELL), r = Math.floor((wy - this.oy) / CELL);
      if (c < 0 || r < 0 || c >= this.cols || r >= this.rows || !this.mask[r * this.cols + c]) continue;
      // Densité variable : des plaques plus fournies, d'autres clairsemées
      const patch = 0.5 + 0.5 * Math.sin(wx * 0.11 + Math.sin(wy * 0.07) * 2) * Math.cos(wy * 0.09);
      const k = hash(wx, wy, 1);
      if (k > PER_CELL * (0.35 + patch * 1.1)) continue;
      p.set(wx + hash(wx, wy, 2) * CELL, 0, wy + hash(wx, wy, 3) * CELL);
      q.setFromAxisAngle(up, hash(wx, wy, 4) * 6.28);
      const sc = 0.6 + hash(wx, wy, 5) * 0.4 + patch * 0.2;
      s.set(sc, sc * (0.8 + hash(wx, wy, 6) * 0.5), sc);
      mats.push(new THREE.Matrix4().compose(p, q, s));
      // Couleur : vert de l'herbe du sol, plus ou moins sec
      const dry = hash(wx, wy, 7);
      colors.push(new THREE.Color(0x5b6a30).lerp(new THREE.Color(0x8a8a3e), dry * dry * 0.8).multiplyScalar(0.9 + hash(wx, wy, 8) * 0.25));
    }
    if (!mats.length) return null;
    const inst = new THREE.InstancedMesh(this.geo, this.mat, mats.length);
    mats.forEach((m, i) => { inst.setMatrixAt(i, m); inst.setColorAt(i, colors[i]!); });
    inst.receiveShadow = true;
    inst.castShadow = false;
    inst.frustumCulled = true;
    inst.computeBoundingSphere();
    this.root.add(inst);
    return inst;
  }

  // Appelée à chaque image : carrés visibles autour du point de vue
  update(x: number, y: number, on = true) {
    this.root.visible = on;
    if (!on) return;
    const r = Math.ceil(VIEW / CHUNK), cx0 = Math.floor(x / CHUNK), cy0 = Math.floor(y / CHUNK);
    let built = 0;
    for (const [key, m] of this.chunks) if (m) { const [a, b] = key.split(',').map(Number) as [number, number]; m.visible = Math.hypot((a + 0.5) * CHUNK - x, (b + 0.5) * CHUNK - y) < VIEW + CHUNK * 0.7; }
    for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) {
      const cx = cx0 + dx, cy = cy0 + dy, key = `${cx},${cy}`;
      if (this.chunks.has(key) || Math.hypot((cx + 0.5) * CHUNK - x, (cy + 0.5) * CHUNK - y) > VIEW + CHUNK * 0.7) continue;
      if (built++ >= 2) return; // pas plus de deux carrés construits par image (pas d'à-coup)
      this.chunks.set(key, this.build(cx, cy));
    }
  }
}
